import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { NotebookPen, Siren, ChevronRight } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { STEPS } from "../data/courseData";

export default function JournalHistory() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/journal").then((r) => setEntries(r.data)); }, []);

  return (
    <Layout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="bps-eyebrow">Journal</div>
        <h1 className="font-display text-3xl font-light tracking-tighter mt-1">Your entries</h1>
        <p className="text-sm text-[#9AAEC6] mt-2">Each entry is a small rewiring of the nervous system.</p>

        {/* Quick links to other reflection spaces */}
        <div className="mt-5 space-y-3" data-testid="journal-quick-links">
          <Link
            to="/notes"
            data-testid="notes-link"
            className="block bps-card p-5 flex items-center gap-4 hover:-translate-y-0.5 hover:border-[#22D3EE]/30 transition-all group"
          >
            <div className="w-14 h-14 rounded-full bg-[#22D3EE]/15 flex items-center justify-center shrink-0">
              <NotebookPen size={22} className="text-[#22D3EE]" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base text-white">My notes</div>
              <div className="text-xs text-[#9AAEC6] mt-0.5 leading-snug">
                Every reflection you've written across the 4-week journey — in one place.
              </div>
            </div>
            <ChevronRight size={18} className="text-[#7C8FA8] group-hover:text-[#22D3EE] transition-colors" />
          </Link>

          <Link
            to="/flare-log"
            data-testid="flare-log-link"
            className="block bps-card p-5 flex items-center gap-4 hover:-translate-y-0.5 hover:border-[#F87171]/30 transition-all group"
          >
            <div className="w-14 h-14 rounded-full bg-[#F87171]/15 flex items-center justify-center shrink-0">
              <Siren size={22} className="text-[#F87171]" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base text-white">Flare reflections</div>
              <div className="text-xs text-[#9AAEC6] mt-0.5 leading-snug">
                Every Pain SOS log — the moment, the trigger, the pattern.
              </div>
            </div>
            <ChevronRight size={18} className="text-[#7C8FA8] group-hover:text-[#F87171] transition-colors" />
          </Link>
        </div>

        <div className="mt-7 mb-3 bps-eyebrow">Practice 4 entries</div>
        <div className="space-y-3" data-testid="journal-entries">
          {entries.length === 0 && (
            <div className="bps-card p-6 text-center text-sm text-[#9AAEC6]">No entries yet. Begin a step to write your first one.</div>
          )}
          {entries.map((e) => {
            const step = STEPS.find((s) => s.step === e.step);
            return (
              <div key={e.id} className="bps-card p-5" data-testid={`journal-entry-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div className="bps-eyebrow" style={{ color: step?.accent }}>Week {e.step} · {step?.title}</div>
                  <div className="text-xs text-[#7C8FA8]">{new Date(e.created_at).toLocaleDateString()}</div>
                </div>
                {e.emotion && e.opposite_emotion && (
                  <div className="mt-2 inline-flex items-center gap-2 text-sm">
                    <span className="text-[#F87171]">{e.emotion}</span>
                    <span className="text-[#7C8FA8]">→</span>
                    <span className="text-[#10B981]">{e.opposite_emotion}</span>
                  </div>
                )}
                {e.visualization && <p className="mt-2 text-sm text-white line-clamp-3">{e.visualization}</p>}
                {(e.intensity_before != null && e.intensity_after != null) && (
                  <div className="mt-3 text-xs text-[#9AAEC6]">Intensity: {e.intensity_before} → {e.intensity_after}</div>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
    </Layout>
  );
}
